import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Container, Dropdown, Nav, Navbar as BSNavbar, Spinner } from 'react-bootstrap';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUserRole, logout } from '../../utils/auth';
import { resolveSafeOperatorRoute } from '../../utils/internalRouteGuard';
import {
  APP_NOTIFICATIONS_CHANGED_EVENT,
  AppNotification,
  appNotificationService,
} from '../../services/appNotificationService';
import CreatePaymentLinkModal from '../PaymentLinks/CreatePaymentLinkModal';
import ChangePasswordModal from './ChangePasswordModal';
import { scheduleScrollPositionReset } from './RouteScrollReset';

type NotificationFilter = 'all' | 'payments' | 'ops' | 'webhooks';

const NOTIFICATION_FILTERS: Array<{ key: NotificationFilter; label: string }> = [
  { key: 'all', label: 'All activity' },
  { key: 'payments', label: 'Payments' },
  { key: 'ops', label: 'Follow-up' },
  { key: 'webhooks', label: 'Delivery' },
];

const buildNavbarNotificationSignature = (notifications: AppNotification[]) => JSON.stringify(
  notifications.map((notification) => ({
    id: notification.id,
    type: notification.type,
    severity: notification.severity,
    createdAt: notification.createdAt,
    readAt: notification.readAt,
    title: notification.title,
    transactionId: notification.transactionId,
  })),
);

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = getCurrentUserRole();
  const [expanded, setExpanded] = useState(false);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState<NotificationFilter>('all');
  const [showNotificationsMenu, setShowNotificationsMenu] = useState(false);
  const notificationsSignatureRef = useRef<string>('');
  const unreadCountRef = useRef<number>(0);

  const handleLogout = () => {
    setExpanded(false);
    logout();
  };

  const handleNavClick = () => {
    setExpanded(false);
    scheduleScrollPositionReset();
  };

  const handlePaymentLinkSuccess = (paymentLink: string) => {
    const clipboard = navigator?.clipboard;
    if (!clipboard?.writeText) {
      return;
    }

    clipboard.writeText(paymentLink).catch(() => {
    });
  };

  const getNotificationCategory = (notificationType: string): NotificationFilter => {
    switch (notificationType) {
      case 'PaymentReceived':
      case 'InvoiceIssued':
      case 'InvoicePaid':
      case 'UnderpaymentReceived':
        return 'payments';
      case 'WebhookRetrying':
      case 'WebhookFailed':
        return 'webhooks';
      default:
        return 'ops';
    }
  };

  const getNotificationTone = (severity: string) => {
    switch (severity) {
      case 'Success':
        return 'success';
      case 'Critical':
        return 'critical';
      case 'Warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatNotificationTime = (createdAt: string) => (
    new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(createdAt))
  );

  const loadNotifications = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoadingNotifications(true);
      }

      const items = await appNotificationService.getNotifications(20, false);
      const nextSignature = buildNavbarNotificationSignature(items);

      if (nextSignature !== notificationsSignatureRef.current) {
        notificationsSignatureRef.current = nextSignature;
        setNotifications(items);
      }
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      if (!silent) {
        setLoadingNotifications(false);
      }
    }
  }, []);

  const loadUnreadCount = useCallback(async () => {
    try {
      const unread = await appNotificationService.getUnreadCount();
      if (unread !== unreadCountRef.current) {
        unreadCountRef.current = unread;
        setUnreadCount(unread);
      }
    } catch {
      // Keep notification polling quiet on transient failures.
    }
  }, []);

  const syncNotifications = useCallback(async (silent: boolean = true) => {
    await Promise.all([
      loadUnreadCount(),
      showNotificationsMenu ? loadNotifications(silent) : Promise.resolve(),
    ]);
  }, [loadNotifications, loadUnreadCount, showNotificationsMenu]);

  useEffect(() => {
    void syncNotifications();

    const handle = window.setInterval(() => {
      void syncNotifications();
    }, 15000);

    const handleNotificationsChanged = () => {
      void syncNotifications();
    };

    const handleFocus = () => {
      void syncNotifications();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncNotifications();
      }
    };

    window.addEventListener(APP_NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(handle);
      window.removeEventListener(APP_NOTIFICATIONS_CHANGED_EVENT, handleNotificationsChanged);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncNotifications]);

  useEffect(() => {
    if (!showNotificationsMenu) {
      return undefined;
    }

    const menuRefreshHandle = window.setInterval(() => {
      void syncNotifications();
    }, 10000);

    return () => {
      window.clearInterval(menuRefreshHandle);
    };
  }, [showNotificationsMenu, syncNotifications]);

  const handleOpenNotification = async (notification: AppNotification) => {
    try {
      if (!notification.readAt && notification.transactionId) {
        await appNotificationService.markTransactionNotificationsAsRead(notification.transactionId);
      } else if (!notification.readAt) {
        await appNotificationService.markAsRead(notification.id);
      }
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    } finally {
      void syncNotifications();
    }

    const safeRoute = resolveSafeOperatorRoute(notification.linkUrl);
    if (safeRoute) {
      scheduleScrollPositionReset();
      navigate(safeRoute);
      return;
    }

    if (notification.transactionId) {
      scheduleScrollPositionReset();
      navigate(`/transactions?search=${notification.transactionId}&openTransactionId=${notification.transactionId}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await appNotificationService.markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    } finally {
      void syncNotifications();
    }
  };

  const filteredNotifications = notifications.filter((notification) => (
    notificationFilter === 'all' ? true : getNotificationCategory(notification.type) === notificationFilter
  ));

  const notificationsByFilter = NOTIFICATION_FILTERS.reduce<Record<NotificationFilter, number>>((counts, filter) => {
    counts[filter.key] = filter.key === 'all'
      ? notifications.length
      : notifications.filter((notification) => getNotificationCategory(notification.type) === filter.key).length;
    return counts;
  }, {
    all: notifications.length,
    payments: 0,
    ops: 0,
    webhooks: 0,
  });

  const selectedFilterLabel = NOTIFICATION_FILTERS.find((filter) => filter.key === notificationFilter)?.label || 'All activity';
  const emptyNotificationMessage = notificationFilter === 'all'
    ? 'New activity will show up here as payments, delivery updates, or review tasks come in.'
    : `No ${selectedFilterLabel.toLowerCase()} items are waiting right now.`;

  const renderPrimaryNavLink = (to: string, label: string, end: boolean = false) => (
    <Nav.Item>
      <NavLink
        to={to}
        end={end}
        onClick={handleNavClick}
        className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
      >
        {label}
      </NavLink>
    </Nav.Item>
  );

  useEffect(() => {
    setExpanded(false);
    setShowNotificationsMenu(false);
  }, [location.pathname]);

  return (
    <BSNavbar expand="lg" expanded={expanded} onToggle={(nextExpanded) => setExpanded(Boolean(nextExpanded))} className="app-navbar">
      <Container fluid="xl">
        <BSNavbar.Brand as={Link} to="/dashboard" className="app-navbar__brand">
          <span className="app-navbar__brand-mark">
            <img src="/rizzpay-mark.png" alt="RizzPay" className="app-navbar__brand-logo" />
          </span>
          <span>
            <span className="app-navbar__eyebrow">Payment Operations</span>
            <span className="app-navbar__wordmark">RizzPay</span>
          </span>
        </BSNavbar.Brand>

        <BSNavbar.Toggle aria-controls="rizz-navbar-nav" className="app-navbar__toggle" />

        <BSNavbar.Collapse id="rizz-navbar-nav" className="app-navbar__collapse">
          <Nav className="me-auto app-navbar__links">
            {renderPrimaryNavLink('/dashboard', 'Dashboard', true)}
            {userRole !== 'Agent' ? renderPrimaryNavLink('/users', 'Users') : null}
            {renderPrimaryNavLink('/transactions', 'Transactions')}
            {renderPrimaryNavLink('/settlements', 'Settlements')}
            {renderPrimaryNavLink('/reconciliation', 'Reconciliation')}
            {(userRole === 'Admin' || userRole === 'SuperAgent' || userRole === 'Agent')
              ? renderPrimaryNavLink('/settings', 'Settings')
              : null}
          </Nav>

          <Nav className="app-navbar__actions">
            <Dropdown
              align="end"
              className="app-toolbar-dropdown"
              onToggle={(isOpen) => {
                setShowNotificationsMenu(isOpen);
                if (isOpen) {
                  void syncNotifications(false);
                }
              }}
            >
              <Dropdown.Toggle
                variant="outline-light"
                id="navbar-notifications-toggle"
                className="app-toolbar-icon-btn"
                aria-label="Open activity notifications"
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="app-toolbar-icon-btn__icon"
                >
                  <path
                    d="M12 3.75a4.5 4.5 0 0 0-4.5 4.5v1.28c0 .68-.2 1.34-.56 1.92l-1.36 2.18a1.75 1.75 0 0 0 1.49 2.67h9.86a1.75 1.75 0 0 0 1.49-2.67l-1.36-2.18a3.64 3.64 0 0 1-.56-1.92V8.25a4.5 4.5 0 0 0-4.5-4.5Zm0 16.5a2.63 2.63 0 0 1-2.47-1.75h4.94A2.63 2.63 0 0 1 12 20.25Z"
                    fill="currentColor"
                  />
                </svg>
                <span className="visually-hidden">Activity</span>
                {unreadCount > 0 ? (
                  <Badge bg="danger" pill className="app-toolbar-icon-btn__badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Badge>
                ) : null}
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="app-notifications-menu"
              >
                <div className="app-notifications-menu__header">
                  <div className="app-notifications-menu__header-main">
                    <div className="app-notifications-menu__eyebrow">Workspace activity</div>
                    <div className="app-notifications-menu__title">Stay on top of what needs attention</div>
                    <div className="app-notifications-menu__subtext">
                      {unreadCount > 0
                        ? `${unreadCount} new items are ready for review.`
                        : 'You are all caught up for now.'}
                    </div>
                  </div>
                  <div className="app-notifications-menu__header-actions">
                    <Button
                      variant="outline-light"
                      size="sm"
                      className="app-notifications-menu__clear-btn"
                      disabled={unreadCount === 0}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        void handleMarkAllAsRead();
                      }}
                    >
                      Clear new items
                    </Button>
                  </div>
                </div>

                <div className="app-notifications-menu__filters">
                  <div className="app-notifications-menu__filters-copy">
                    Open any item to jump straight to the right page.
                  </div>
                  <div className="app-notifications-menu__filter-list">
                    {NOTIFICATION_FILTERS.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        className={`app-notifications-menu__filter${notificationFilter === filter.key ? ' app-notifications-menu__filter--active' : ''}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setNotificationFilter(filter.key);
                        }}
                      >
                        <span>{filter.label}</span>
                        <span className="app-notifications-menu__filter-count">
                          {notificationsByFilter[filter.key]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="app-notifications-menu__list">
                  {loadingNotifications && showNotificationsMenu ? (
                    <div className="app-notifications-menu__loading">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : filteredNotifications.length === 0 ? (
                    <div className="app-notifications-menu__empty">
                      <div className="app-notifications-menu__empty-title">Nothing waiting here</div>
                      <div className="app-notifications-menu__empty-copy">
                        {emptyNotificationMessage}
                      </div>
                    </div>
                  ) : (
                    filteredNotifications.map((notification) => {
                      const category = getNotificationCategory(notification.type);
                      const tone = getNotificationTone(notification.severity);

                      return (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => void handleOpenNotification(notification)}
                          className="app-notifications-menu__item"
                          data-state={notification.readAt ? 'read' : 'unread'}
                          data-severity={tone}
                        >
                          <div className="app-notifications-menu__item-topline">
                            <span className="app-notifications-menu__item-category">
                              {category === 'payments'
                                ? 'Payments'
                                : category === 'webhooks'
                                  ? 'Delivery'
                                  : 'Follow-up'}
                            </span>
                            {!notification.readAt ? (
                              <span className="app-notifications-menu__item-status">New</span>
                            ) : null}
                          </div>
                          <div className="d-flex justify-content-between align-items-start gap-3">
                            <div className="app-notifications-menu__item-main">
                              <div className="app-notifications-menu__item-title">
                                {notification.title}
                              </div>
                              <div className="app-notifications-menu__item-message">
                                {notification.message}
                              </div>
                              <div className="app-notifications-menu__item-time">
                                {formatNotificationTime(notification.createdAt)}
                              </div>
                            </div>
                            <span className="app-notifications-menu__item-chevron">Open</span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                <div className="app-notifications-menu__footer">
                  Showing {filteredNotifications.length} {filteredNotifications.length === 1 ? 'item' : 'items'} in {selectedFilterLabel.toLowerCase()}.
                </div>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown align="end" className="app-toolbar-dropdown">
              <Dropdown.Toggle
                variant="success"
                id="navbar-create-toggle"
                className="app-toolbar-btn app-toolbar-btn--primary"
              >
                Payments
              </Dropdown.Toggle>

              <Dropdown.Menu className="app-toolbar-menu">
                <button
                  type="button"
                  className="app-toolbar-menu__item"
                  onClick={() => {
                    setExpanded(false);
                    setShowPaymentLinkModal(true);
                  }}
                >
                  <span className="app-toolbar-menu__item-title">New payment</span>
                  <span className="app-toolbar-menu__item-copy">Create a fresh payment link for direct crypto or swap checkout.</span>
                </button>
                <button
                  type="button"
                  className="app-toolbar-menu__item"
                  onClick={() => {
                    setExpanded(false);
                    setShowInvoiceModal(true);
                  }}
                >
                  <span className="app-toolbar-menu__item-title">New invoice</span>
                  <span className="app-toolbar-menu__item-copy">Issue an invoice-style payment request without leaving the current page.</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown align="end" className="app-toolbar-dropdown">
              <Dropdown.Toggle
                variant="outline-light"
                id="navbar-account-toggle"
                className="app-toolbar-btn app-toolbar-btn--ghost"
              >
                Profile
              </Dropdown.Toggle>

              <Dropdown.Menu className="app-toolbar-menu">
                <button
                  type="button"
                  className="app-toolbar-menu__item"
                  onClick={() => {
                    setExpanded(false);
                    setShowChangePasswordModal(true);
                  }}
                >
                  <span className="app-toolbar-menu__item-title">Change password</span>
                  <span className="app-toolbar-menu__item-copy">Update your login credentials without leaving the workspace.</span>
                </button>
                <button
                  type="button"
                  className="app-toolbar-menu__item app-toolbar-menu__item--danger"
                  onClick={handleLogout}
                >
                  <span className="app-toolbar-menu__item-title">Logout</span>
                  <span className="app-toolbar-menu__item-copy">End this session and return to the sign-in screen.</span>
                </button>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </BSNavbar.Collapse>
      </Container>

      <CreatePaymentLinkModal
        show={showPaymentLinkModal}
        onHide={() => setShowPaymentLinkModal(false)}
        onSuccess={handlePaymentLinkSuccess}
      />

      <CreatePaymentLinkModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        onSuccess={handlePaymentLinkSuccess}
        mode="invoice"
      />

      <ChangePasswordModal
        show={showChangePasswordModal}
        onHide={() => setShowChangePasswordModal(false)}
      />
    </BSNavbar>
  );
};

export default Navbar;
